/**
 * Spreadsheet import for the Create RFQ wizard's Line Items step — CR-6204.
 *
 * Everything happens in the browser: the RFQ does not exist until the wizard
 * is submitted, so imported rows simply become wizard lines the user can edit
 * (and that the wizard draft saves) like any typed line.
 *
 * Limits mirror the backend RFQLineItem model so a row accepted here cannot
 * fail at submit: part_number max 100, unit_of_measure max 20, drawing_issue
 * max 64, quantity / target_unit_price DecimalField(max_digits=12, 2 dp).
 * Quantity is a whole number, as the line card itself only accepts one.
 */
import * as XLSX from 'xlsx';
import { EMPTY_LINE_ITEM } from './rfqWizardTypes';
import type { LineItemForm } from './rfqWizardTypes';

/** File types the import accepts (also the file picker's `accept`). */
export const IMPORT_ACCEPT = '.xlsx,.xls,.csv';
/** Largest file read, in bytes. */
export const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;
/** Most line items one file may carry. */
export const MAX_IMPORT_ROWS = 500;
/** Downloaded template's file name. */
export const TEMPLATE_FILE_NAME = 'RFQ line items template.xlsx';

const MAX_AMOUNT = 9999999999.99;
const HEADER_SEARCH_ROWS = 10;

type ImportField =
  | 'item_description'
  | 'part_number'
  | 'quantity'
  | 'unit_of_measure'
  | 'target_unit_price'
  | 'drawing_issue'
  | 'technical_specifications';

interface ImportColumn {
  field: ImportField;
  /** Label as shown on the line card and in messages. */
  label: string;
  required: boolean;
  /** Normalised header spellings accepted for this column. */
  aliases: string[];
}

/** Template columns, in template order. Labels match the line card. */
export const IMPORT_COLUMNS: ImportColumn[] = [
  { field: 'item_description', label: 'Description', required: true, aliases: ['description', 'item description', 'part description'] },
  { field: 'part_number', label: 'Part Number', required: true, aliases: ['part number', 'part no', 'part num', 'part', 'pn'] },
  { field: 'quantity', label: 'Quantity', required: true, aliases: ['quantity', 'qty'] },
  { field: 'unit_of_measure', label: 'Unit', required: false, aliases: ['unit', 'uom', 'unit of measure'] },
  { field: 'target_unit_price', label: 'Target Unit Price', required: false, aliases: ['target unit price', 'unit price', 'target price', 'price'] },
  { field: 'drawing_issue', label: 'Drawing Issue / Revision', required: false, aliases: ['drawing issue revision', 'drawing issue', 'drawing revision', 'revision', 'rev'] },
  { field: 'technical_specifications', label: 'Technical Specifications', required: false, aliases: ['technical specifications', 'technical specification', 'specifications', 'specification', 'tech specs', 'specs'] },
];

const MAX_LENGTH: Partial<Record<ImportField, number>> = {
  part_number: 100,
  unit_of_measure: 20,
  drawing_issue: 64,
};

/** A spreadsheet row that was skipped, with every reason found. */
export interface RowProblem {
  /** Row number as Excel shows it. */
  row: number;
  messages: string[];
}

export type LineItemImportResult =
  | { ok: true; lines: LineItemForm[]; problems: RowProblem[] }
  | { ok: false; error: string };

const fail = (error: string): LineItemImportResult => ({ ok: false, error });

/* ------------------------------------------------------------------ */
/*  Template                                                           */
/* ------------------------------------------------------------------ */

/**
 * Build the downloadable template: a Line Items sheet with the headers and an
 * Instructions sheet explaining each column.
 *
 * @returns the workbook
 */
export function buildLineItemTemplate(): XLSX.WorkBook {
  const headers = IMPORT_COLUMNS.map((c) => (c.required ? `${c.label} *` : c.label));
  const sheet = XLSX.utils.aoa_to_sheet([headers]);
  sheet['!cols'] = [{ wch: 40 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 24 }, { wch: 50 }];

  const instructions = XLSX.utils.aoa_to_sheet([
    ['How to fill in the Line Items sheet'],
    [],
    ['Column', 'Required', 'What to enter'],
    ['Description', 'Yes', 'What the supplier is quoting for.'],
    ['Part Number', 'Yes', 'Up to 100 characters. If your part numbers start with 0, format the column as Text before typing them.'],
    ['Quantity', 'Yes', 'A whole number above 0.'],
    ['Unit', 'No', 'Up to 20 characters, e.g. Each, kg, m. Left blank, it is set to Each.'],
    ['Target Unit Price', 'No', 'A number of 0 or more with at most 2 decimal places, without the currency symbol.'],
    ['Drawing Issue / Revision', 'No', 'Up to 64 characters, e.g. Rev B, Issue 3.'],
    ['Technical Specifications', 'No', 'Any length.'],
    [],
    [`One row per line item, up to ${MAX_IMPORT_ROWS} rows. Blank rows are ignored.`],
    ['Rows with a problem are skipped and listed with the reason; every other row is imported.'],
    ['Imported lines are added after the lines already in the RFQ, and can be edited before you continue.'],
  ]);
  instructions['!cols'] = [{ wch: 26 }, { wch: 10 }, { wch: 90 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Line Items');
  XLSX.utils.book_append_sheet(workbook, instructions, 'Instructions');
  return workbook;
}

/** Download the template to the user's machine. */
export function downloadLineItemTemplate(): void {
  XLSX.writeFile(buildLineItemTemplate(), TEMPLATE_FILE_NAME);
}

/* ------------------------------------------------------------------ */
/*  Reading cells                                                      */
/* ------------------------------------------------------------------ */

const normaliseHeader = (value: string): string =>
  value.toLowerCase().replace(/[*#./\\_\-():]/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Text of a cell as the user sees it. Numbers keep their display format (a
 * part number formatted 00123 stays 00123), except when Excel would show them
 * in scientific notation, where the full value is used instead.
 */
function cellText(cell: XLSX.CellObject | undefined): string {
  if (!cell || cell.v === undefined || cell.v === null) return '';
  switch (cell.t) {
    case 'n': {
      const shown = cell.w;
      return (shown && !/e[+-]?\d/i.test(shown) ? shown : String(cell.v)).trim();
    }
    case 'b':
      return cell.v ? 'TRUE' : 'FALSE';
    case 'e':
    case 'z':
      return '';
    case 'd':
      return (cell.w ?? String(cell.v)).trim();
    default:
      return String(cell.v).trim();
  }
}

/** A cell's number: null when blank, NaN when it is not a number. */
function cellNumber(cell: XLSX.CellObject | undefined, allowCurrencySymbol: boolean): number | null {
  if (cell && cell.t === 'n' && typeof cell.v === 'number') return cell.v;
  let text = cellText(cell).replace(/\s+/g, '');
  if (text === '') return null;
  if (allowCurrencySymbol) text = text.replace(/^[£$€₹]/, '');
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(text)) text = text.replace(/,/g, '');
  return /^-?(\d+(\.\d*)?|\.\d+)$/.test(text) ? Number(text) : NaN;
}

const hasAtMostTwoDecimals = (value: number): boolean => Math.abs(Math.round(value * 100) - value * 100) < 1e-6;

/* ------------------------------------------------------------------ */
/*  Parsing                                                            */
/* ------------------------------------------------------------------ */

/** Populated cells of a sheet grouped by row, in row order. */
function rowsOf(sheet: XLSX.WorkSheet): Array<{ row: number; cells: Map<number, XLSX.CellObject> }> {
  const byRow = new Map<number, Map<number, XLSX.CellObject>>();
  Object.keys(sheet).forEach((address) => {
    if (address.startsWith('!')) return;
    const { r, c } = XLSX.utils.decode_cell(address);
    const cell = sheet[address] as XLSX.CellObject;
    if (!byRow.has(r)) byRow.set(r, new Map());
    byRow.get(r)?.set(c, cell);
  });
  return Array.from(byRow.entries()).sort((a, b) => a[0] - b[0]).map(([row, cells]) => ({ row, cells }));
}

/** Map of field → column index for a candidate header row. */
function matchHeaders(cells: Map<number, XLSX.CellObject>): Map<ImportField, number> {
  const found = new Map<ImportField, number>();
  Array.from(cells.entries()).sort((a, b) => a[0] - b[0]).forEach(([col, cell]) => {
    const header = normaliseHeader(cellText(cell));
    const column = IMPORT_COLUMNS.find((c) => c.aliases.includes(header));
    if (column && !found.has(column.field)) found.set(column.field, col);
  });
  return found;
}

const REQUIRED = IMPORT_COLUMNS.filter((c) => c.required);

/** Validate one data row; returns the line or the reasons it was skipped. */
function parseRow(cells: Map<number, XLSX.CellObject>, columns: Map<ImportField, number>): LineItemForm | string[] {
  const cellFor = (field: ImportField) => {
    const col = columns.get(field);
    return col === undefined ? undefined : cells.get(col);
  };
  const text = (field: ImportField) => cellText(cellFor(field));
  const messages: string[] = [];

  IMPORT_COLUMNS.forEach(({ field, label, required }) => {
    if (field === 'quantity' || field === 'target_unit_price') return;
    const value = text(field);
    if (required && value === '') messages.push(`${label} is required`);
    const max = MAX_LENGTH[field];
    if (max !== undefined && value.length > max) messages.push(`${label} is longer than ${max} characters`);
  });

  const quantity = cellNumber(cellFor('quantity'), false);
  if (quantity === null) {
    messages.push('Quantity is required');
  } else if (!Number.isInteger(quantity) || quantity <= 0) {
    messages.push('Quantity must be a whole number above 0');
  } else if (quantity > MAX_AMOUNT) {
    messages.push('Quantity is too large');
  }

  const price = cellNumber(cellFor('target_unit_price'), true);
  if (price !== null && (Number.isNaN(price) || price < 0 || !hasAtMostTwoDecimals(price))) {
    messages.push('Target Unit Price must be a number of 0 or more with at most 2 decimal places');
  } else if (price !== null && price > MAX_AMOUNT) {
    messages.push('Target Unit Price is too large');
  }

  if (messages.length > 0) return messages;
  return {
    ...EMPTY_LINE_ITEM,
    item_description: text('item_description'),
    part_number: text('part_number'),
    quantity: quantity as number,
    unit_of_measure: text('unit_of_measure') || EMPTY_LINE_ITEM.unit_of_measure,
    target_unit_price: price,
    drawing_issue: text('drawing_issue'),
    technical_specifications: text('technical_specifications'),
  };
}

/**
 * Turn a workbook into wizard lines.
 *
 * Uses the first sheet whose first rows hold the Description, Part Number and
 * Quantity headers (so a title row above the headers, extra columns or a
 * different column order are fine). Blank rows are ignored; rows with a
 * problem are skipped and reported with their Excel row number.
 *
 * @param workbook - the parsed spreadsheet
 * @returns the lines and skipped rows, or a reason the whole file was refused
 */
export function parseLineItemWorkbook(workbook: XLSX.WorkBook): LineItemImportResult {
  let best: { missing: string[] } | null = null;

  for (const name of workbook.SheetNames) {
    const rows = rowsOf(workbook.Sheets[name]);
    for (let i = 0; i < Math.min(rows.length, HEADER_SEARCH_ROWS); i += 1) {
      const columns = matchHeaders(rows[i].cells);
      const missing = REQUIRED.filter((c) => !columns.has(c.field)).map((c) => c.label);
      if (missing.length > 0) {
        if (missing.length < REQUIRED.length && (!best || missing.length < best.missing.length)) best = { missing };
        continue;
      }

      const data = rows.slice(i + 1).filter(({ cells }) =>
        Array.from(columns.values()).some((col) => cellText(cells.get(col)) !== ''));
      if (data.length === 0) return fail('The file has no line items under the headers.');
      if (data.length > MAX_IMPORT_ROWS) {
        return fail(`The file has ${data.length} line items; import up to ${MAX_IMPORT_ROWS} at a time.`);
      }

      const lines: LineItemForm[] = [];
      const problems: RowProblem[] = [];
      data.forEach(({ row, cells }) => {
        const parsed = parseRow(cells, columns);
        if (Array.isArray(parsed)) problems.push({ row: row + 1, messages: parsed });
        else lines.push(parsed);
      });
      return { ok: true, lines, problems };
    }
  }

  if (best) {
    return fail(`The file has no ${best.missing.join(' or ')} column. Download the template to see the expected columns.`);
  }
  return fail('No Description, Part Number and Quantity columns found. Download the template to see the expected columns.');
}

/**
 * Decode a CSV's bytes. SheetJS reads raw CSV bytes one byte per character,
 * which turns UTF-8 "Café" into "CafÃ©", so the text is decoded here: UTF-8
 * (Excel's "CSV UTF-8"), falling back to Windows-1252 (Excel's plain "CSV").
 */
function decodeCsv(data: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(data).replace(/^﻿/, '');
  } catch {
    return new TextDecoder('windows-1252').decode(data);
  }
}

/**
 * Read an uploaded file's bytes and turn them into wizard lines.
 *
 * Text is read without value guessing, so a CSV part number such as 00123
 * keeps its leading zeros; numbers are parsed per column instead.
 *
 * @param data - the file's contents
 * @param fileName - used to check the file type
 * @returns as parseLineItemWorkbook
 */
export function parseLineItemFile(data: ArrayBuffer, fileName: string): LineItemImportResult {
  const extension = fileName.toLowerCase().split('.').pop() ?? '';
  if (!IMPORT_ACCEPT.split(',').includes(`.${extension}`)) {
    return fail('Upload an Excel (.xlsx, .xls) or CSV file.');
  }
  if (data.byteLength > MAX_IMPORT_FILE_BYTES) {
    return fail('The file is larger than 5 MB.');
  }
  let workbook: XLSX.WorkBook;
  try {
    workbook = extension === 'csv'
      ? XLSX.read(decodeCsv(data), { type: 'string', raw: true })
      : XLSX.read(data, { type: 'array', raw: true });
  } catch {
    return fail("The file couldn't be read. Save it as .xlsx or .csv and try again.");
  }
  return parseLineItemWorkbook(workbook);
}

/* ------------------------------------------------------------------ */
/*  Merging into the wizard                                            */
/* ------------------------------------------------------------------ */

/** A line nobody has typed into — the wizard's starting line, or an unused "Add Item". */
export function isBlankLine(line: LineItemForm): boolean {
  // CR-6205: a line someone has attached a file to is in use, even before
  // they type anything, so an import must not drop it.
  return !(line.files?.length || line.repick_file_names?.length)
    && !line.item_description.trim()
    && !line.part_number.trim()
    && !(line.drawing_issue ?? '').trim()
    && !line.technical_specifications.trim()
    && line.target_unit_price === null
    && line.quantity === EMPTY_LINE_ITEM.quantity
    && line.unit_of_measure === EMPTY_LINE_ITEM.unit_of_measure;
}

/** Everything a supplier would see on the line, for spotting a line that is already there. */
const lineKey = (line: LineItemForm): string => JSON.stringify([
  line.item_description.trim(),
  line.part_number.trim(),
  line.quantity,
  line.unit_of_measure.trim(),
  line.target_unit_price,
  (line.drawing_issue ?? '').trim(),
  line.technical_specifications.trim(),
]);

export interface MergedLines {
  /** The wizard's new line list (never empty). */
  lines: LineItemForm[];
  /** Imported lines actually added. */
  added: number;
  /** Imported lines skipped because an identical line was already in the RFQ. */
  alreadyPresent: number;
}

/**
 * Add imported lines after the existing ones.
 *
 * Blank lines are dropped so the wizard's empty starting line doesn't sit
 * above the import. A line identical to one already in the RFQ is not added
 * again, so fixing the skipped rows and re-importing the same file adds only
 * those rows.
 *
 * @param existing - the wizard's current lines
 * @param imported - lines from the spreadsheet
 * @returns the new list and what was added
 */
export function mergeImportedLines(existing: LineItemForm[], imported: LineItemForm[]): MergedLines {
  const present = new Set(existing.map(lineKey));
  const fresh = imported.filter((line) => !present.has(lineKey(line)));
  if (fresh.length === 0) return { lines: existing, added: 0, alreadyPresent: imported.length };
  return {
    lines: [...existing.filter((line) => !isBlankLine(line)), ...fresh],
    added: fresh.length,
    alreadyPresent: imported.length - fresh.length,
  };
}
